import {StateStatus} from "../../../shared/models/state-status.models";
import {createReducer, on} from "@ngrx/store";

import {BuildKitReport} from "../../models/build-kit-report.model";
import {
  downloadBuildKitReport,
  downloadBuildKitReportFailure,
  downloadBuildKitReportSuccess,
  generateBuildKitReport,
  generateBuildKitReportFailure,
  generateBuildKitReportSuccess,
  loadBuildKitReports,
  loadBuildKitReportsFailure,
  loadBuildKitReportsSuccess,
  loadBuildKitReportValidationMessages,
  loadBuildKitReportValidationMessagesFailure,
  loadBuildKitReportValidationMessagesSuccess
} from "./build-kit-report.action";
import {ValidationMessage} from "../../../shared/models/cad-model.model";

export interface BuildKitReportStatus {
  status: StateStatus,
  buildKitId: string,
  reportType: string
}
export interface BuildKitReportState {
  buildKitReports: BuildKitReport[];
  buildKitReportStatus: StateStatus;
  buildKitReportDownloadStatus: BuildKitReportStatus[];
  buildKitReportGenerateStatus: BuildKitReportStatus[];
  buildKitValidationMessages: ValidationMessage[];
  buildKitReportValidationMessagesStatus: StateStatus;

  error: any;
}

export const initialState: BuildKitReportState = {
  buildKitReports: [],
  buildKitReportStatus: 'pending' as StateStatus,
  buildKitReportValidationMessagesStatus: 'pending' as StateStatus,
  buildKitReportDownloadStatus: [],
  buildKitReportGenerateStatus: [],
  buildKitValidationMessages: [],
  error: null
};

export const buildKitReportReducer = createReducer(
  initialState,
  on(loadBuildKitReports, (state): BuildKitReportState => ({ ...state, buildKitReportStatus: 'loading' as StateStatus })),
  on(loadBuildKitReportsSuccess, (state, { buildKitReports }): BuildKitReportState => ({
    ...state,
    buildKitReports: buildKitReports,
    error: null,
    buildKitReportStatus: 'loadSuccess' as StateStatus,
  })),
  on(loadBuildKitReportsFailure, (state, { error }): BuildKitReportState => ({
    ...state,
    error: error,
    buildKitReportStatus: 'error' as StateStatus,
  })),

  on(generateBuildKitReport, (state, {reportType, buildKitId}): BuildKitReportState =>
    ({
      ...state,
      buildKitReportGenerateStatus: addOrUpdateBuildKitReportGenerateStatus(state.buildKitReportGenerateStatus, 'generating', reportType, buildKitId)
    })),
  on(generateBuildKitReportSuccess, (state,{ buildKitReport}): BuildKitReportState => ({
    ...state,
    error: null,
    buildKitReports: state.buildKitReports.map((original: BuildKitReport) => {
      return (original.buildKitId == buildKitReport.buildKitId && original.type == buildKitReport.type) ?
        buildKitReport: original;
    }),
    buildKitReportGenerateStatus: addOrUpdateBuildKitReportGenerateStatus(state.buildKitReportGenerateStatus, 'generateSuccess', buildKitReport.type, buildKitReport.buildKitId)
  })),
  on(generateBuildKitReportFailure, (state, { error, reportType, buildKitId }): BuildKitReportState => ({
    ...state,
    error: error,
    buildKitReportGenerateStatus: addOrUpdateBuildKitReportGenerateStatus(state.buildKitReportGenerateStatus, 'generateError', reportType, buildKitId)
  })),

  on(downloadBuildKitReport, (state, {buildKitId,reportType}): BuildKitReportState => ({
    ...state,
    buildKitReportDownloadStatus: addOrUpdateBuildKitReportStatus(state.buildKitReportDownloadStatus, 'downloading', reportType, buildKitId)
  })),
  on(downloadBuildKitReportSuccess, (state, { file, buildKitId,reportType }): BuildKitReportState => ({
    ...state,
    buildKitReportDownloadStatus: addOrUpdateBuildKitReportStatus(state.buildKitReportDownloadStatus, 'downloadSuccess', reportType, buildKitId)
  })),
  on(downloadBuildKitReportFailure, (state, { error, buildKitId,reportType }): BuildKitReportState => ({
    ...state,
    error: error,
    buildKitReportDownloadStatus: addOrUpdateBuildKitReportStatus(state.buildKitReportDownloadStatus, 'downloadError', reportType, buildKitId)
  })),
  on(loadBuildKitReportValidationMessages, (state): BuildKitReportState => ({
    ...state,
    buildKitReportValidationMessagesStatus: 'loading'
  })),
  on(loadBuildKitReportValidationMessagesSuccess, (state, {buildKitReport}): BuildKitReportState => ({
    ...state,
    buildKitReportValidationMessagesStatus: 'loadSuccess',
    buildKitReports: state.buildKitReports.map((reportWithOutMessages: BuildKitReport) => {
      return reportWithOutMessages.id === buildKitReport.id ? buildKitReport : reportWithOutMessages;
    }),
  })),
  on(loadBuildKitReportValidationMessagesFailure, (state): BuildKitReportState => ({
    ...state,
    buildKitReportValidationMessagesStatus: 'loadError'
  })),
);

function addOrUpdateBuildKitReportStatus(buildKitReportStatus: BuildKitReportStatus[], newStatus: StateStatus, reportType: string, buildKitId: string) {
  const bkStatus = buildKitReportStatus.find(bk => bk.reportType == reportType && bk.buildKitId == buildKitId);
  if (bkStatus){
    return buildKitReportStatus.map(bk => bk.reportType == reportType && bk.buildKitId == buildKitId ? {
      ...bk,
      status: newStatus
    } : bk);
  } else {
    return [...buildKitReportStatus, {
      status: newStatus,
      reportType: reportType,
      buildKitId: buildKitId,
    }]
  }
}

function addOrUpdateBuildKitReportGenerateStatus(statuses: BuildKitReportStatus[], newStatus: StateStatus, reportType: string, buildKitId: string) {
  const bkStatus = statuses.find(bk => bk.reportType == reportType && bk.buildKitId == buildKitId);
  if (bkStatus){
    return statuses.map(bk => bk.reportType == reportType && bk.buildKitId == buildKitId ? {
      ...bk,
      status: newStatus
    } : bk);
  } else {
    return [...statuses, {
      status: newStatus,
      reportType: reportType,
      buildKitId: buildKitId,
    }]
  }
}

